import React, {useRef, useState, useLayoutEffect, useId, useCallback} from 'react';

import {findPhotoFrame, isAttachmentOnly, getCropGeometry, clampSourceCrop, zoomCrop, saveCrop, restoreCrop, faceCrop} from './photoCropGeometry';

import {detectFaces} from './faceFinder';



export default function PhotoCropper({photoUrl, cardDesign, faceFinder = false, initialPosition, onCropChange, onReadyChange}) {

  const container = useRef(null), image = useRef(null), geometry = useRef(null), crop = useRef(null);

  const pointers = useRef(new Map()), gesture = useRef(null), touched = useRef(false), faceRun = useRef(false);

  const wheelRef = useRef(() => {});
  const measureRef = useRef(() => {}), callbacks = useRef({onCropChange, onReadyChange});

  callbacks.current = {onCropChange, onReadyChange};

  const [view, setView] = useState(null), [failed, setFailed] = useState(false), [faceStatus, setFaceStatus] = useState('');

  const hint = useId(), frame = findPhotoFrame(cardDesign), attachmentOnly = isAttachmentOnly(cardDesign);

  const aspect = frame ? frame.width / frame.height : null;

  const update = useCallback((value, manual = false) => {

    if (!geometry.current) return;

    if (manual) {touched.current = true; setFaceStatus('');}

    const next = clampSourceCrop(value, geometry.current);

    crop.current = next; setView({g: geometry.current, crop: next});

    callbacks.current.onCropChange?.(saveCrop(next, geometry.current));

    callbacks.current.onReadyChange?.(true);

  }, []);

  useLayoutEffect(() => {

    let cancelled = false;

    geometry.current = null; crop.current = null; pointers.current.clear(); gesture.current = null;

    touched.current = !!initialPosition; faceRun.current = false;

    setView(null); setFailed(false); setFaceStatus(''); callbacks.current.onReadyChange?.(false);

    const measure = () => {

      const img = image.current, box = container.current?.getBoundingClientRect();

      if (!img?.complete || !img.naturalWidth) return;

      if (attachmentOnly) {callbacks.current.onCropChange?.(undefined); callbacks.current.onReadyChange?.(true); return;}

      if (!box || !aspect) return;

      const g = getCropGeometry(box.width, box.height, aspect, img.naturalWidth, img.naturalHeight);

      if (!g) return;

      geometry.current = g; pointers.current.clear(); gesture.current = null;

      update(crop.current || restoreCrop(initialPosition, g));

      if (faceFinder && !faceRun.current && !touched.current) {

        faceRun.current = true; setFaceStatus('Finding face?');

        detectFaces(img).then(result => {

          if (cancelled || touched.current) return;

          update(faceCrop(result.faces, geometry.current));

          setFaceStatus(result.reason === 'unavailable' ? 'Face Finder unavailable in this browser. Drag or zoom to position.' : result.faces.length ? 'Suggested position ? adjust as needed.' : 'No face found ? centered Cover.');

        }).catch(() => {if (!cancelled && !touched.current) setFaceStatus('Face Finder unavailable. Drag or zoom to position.');});

      }

    };

    measureRef.current = measure; measure();

    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;

    if (container.current) observer?.observe(container.current);

    window.addEventListener('resize', measure);

    return () => {cancelled = true; observer?.disconnect(); window.removeEventListener('resize', measure); measureRef.current = () => {};};

  }, [photoUrl, aspect, attachmentOnly, faceFinder, initialPosition, update]);

  useLayoutEffect(() => {
    const el = container.current, handler = event => wheelRef.current(event);
    el?.addEventListener('wheel', handler, {passive: false});
    return () => el?.removeEventListener('wheel', handler);
  }, [attachmentOnly]);
  const point = event => {const r = container.current.getBoundingClientRect(); return {x: event.clientX - r.left, y: event.clientY - r.top};};

  const middle = values => ({x: (values[0].x + values[1].x) / 2, y: (values[0].y + values[1].y) / 2});

  const distance = values => Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);

  function rebase() {const values = [...pointers.current.values()].slice(0,2); gesture.current = crop.current && values.length ? {values, crop: crop.current} : null;}

  function down(event) {

    if (!view || (event.pointerType === 'mouse' && event.button !== 0)) return;

    event.preventDefault(); touched.current = true; setFaceStatus(''); event.currentTarget.setPointerCapture?.(event.pointerId);

    pointers.current.set(event.pointerId, point(event)); rebase();

  }

  function move(event) {

    if (!pointers.current.has(event.pointerId) || !gesture.current) return;

    event.preventDefault(); pointers.current.set(event.pointerId, point(event));

    const start = gesture.current, g = geometry.current, values = [...pointers.current.values()].slice(0,2);

    if (values.length === 1) {

      update({...start.crop, x: start.crop.x - (values[0].x - start.values[0].x) / (g.displayScale * start.crop.scale), y: start.crop.y - (values[0].y - start.values[0].y) / (g.displayScale * start.crop.scale)}, true);

    } else if (start.values.length === 2 && distance(start.values) > 0) {

      const oldMid = middle(start.values), newMid = middle(values);

      const scale = Math.min(8, Math.max(1, start.crop.scale * distance(values) / distance(start.values)));

      update({x: start.crop.x + (oldMid.x - g.frameX) / (g.displayScale * start.crop.scale) - (newMid.x - g.frameX) / (g.displayScale * scale), y: start.crop.y + (oldMid.y - g.frameY) / (g.displayScale * start.crop.scale) - (newMid.y - g.frameY) / (g.displayScale * scale), scale}, true);

    }

  }

  function end(event) {if (!pointers.current.delete(event.pointerId)) return; if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); rebase();}

  function zoom(scale, location) {

    const g = geometry.current; if (!g || !crop.current) return;

    update(zoomCrop(crop.current, scale, location ? (location.x - g.frameX) / g.frameW : .5, location ? (location.y - g.frameY) / g.frameH : .5, g), true); rebase();

  }

  function wheel(event) {if (!crop.current) return; event.preventDefault(); zoom(crop.current.scale * Math.exp(-event.deltaY * .002), point(event));}

  wheelRef.current = wheel;
  function key(event) {

    const directions = {ArrowLeft: [1,0], ArrowRight: [-1,0], ArrowUp: [0,1], ArrowDown: [0,-1]};

    const d = directions[event.key]; if (!d || !geometry.current) return;

    event.preventDefault(); const step = 10 / (geometry.current.displayScale * crop.current.scale);

    update({...crop.current, x: crop.current.x + d[0] * step, y: crop.current.y + d[1] * step}, true); rebase();

  }

  function failImage() {
    geometry.current = null; crop.current = null; pointers.current.clear(); gesture.current = null; touched.current = true;
    setFailed(true); setView(null); callbacks.current.onReadyChange?.(false);
  }
  const g = view?.g, factor = g ? g.displayScale * view.crop.scale : 1;

  return <section className="photo-adjustment" aria-label="Photo adjustment">

    {!attachmentOnly && <p id={hint}>Drag to position ? Pinch or use the zoom control</p>}

    {attachmentOnly ? <><img ref={image} src={photoUrl} alt="Captured attachment" onLoad={() => measureRef.current()} onError={failImage} style={{maxWidth: '90vw', maxHeight: '55dvh', objectFit: 'contain', imageOrientation: 'from-image'}}/><p>This card does not use the photo. Your original will remain attached.</p></> : <>

      {!frame && <p role="alert">A unique photo frame could not be identified. Check the card?s photo binding.</p>}

      <div ref={container} className="photo-crop-area" role="group" aria-label="Reposition photo" aria-describedby={hint} tabIndex={0}

        onPointerDown={down} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end} onKeyDown={key}>

        <img ref={image} key={photoUrl} src={photoUrl} alt="Captured preview" draggable={false} onLoad={() => measureRef.current()}

          onError={failImage}

          style={g ? {visibility: 'visible', width: image.current.naturalWidth, height: image.current.naturalHeight, imageOrientation: 'from-image', transform: `translate(${g.frameX - view.crop.x * factor}px, ${g.frameY - view.crop.y * factor}px) scale(${factor})`} : {visibility: 'hidden'}}/>

        {g && <div className="photo-crop-frame" aria-hidden="true" style={{left: g.frameX, top: g.frameY, width: g.frameW, height: g.frameH}}/>}

      </div>

      {frame && !view && !failed && <p role="status">Preparing photo?</p>}

      {faceStatus && <p role="status">{faceStatus}</p>}

      <div className="photo-position-controls">

        <label>Zoom <input aria-label="Photo zoom" type="range" min="1" max="8" step="0.01" value={view?.crop.scale || 1} disabled={!view || failed} onChange={e => zoom(Number(e.target.value))}/></label>

        <button type="button" className="photo-position-reset" disabled={!view || failed} onClick={() => {update(geometry.current.centerCrop, true); setFaceStatus(''); rebase();}}>Reset</button>

      </div>

    </>}

    {failed && <p role="alert">This photo could not be opened. Choose a JPEG, PNG or WebP image, or retake it.</p>}

  </section>;

}

